import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { reactToastifyConfig } from 'js/config';

/* Atoms */
import { Button } from 'js/components/atoms';

/* Organisms */
import Modal from 'js/components/organisms/Modal';

/* Components */
import RecommendedAccessories from './RecommendedAccessories';

/* Services */
import { addToCart } from 'js/services/cartService';

/* Utilities */
import { trans } from 'js/utilities';
import { getCloudImageUrl } from 'js/utilities/image';
import { getShopData } from 'js/utilities/shopStorage';
import { isVehicleDataComplete } from 'js/utilities/vehicleCompatibility';

/* Symbols */
import cartIcon from 'symbols/cart.svg';
import checkIcon from 'symbols/check.svg';
import arrowIcon from 'symbols/arrow.svg';

export default function AddToCartButton({
    product,
    cartUrl,
    shopwareUrl,
    relatedProductsUrl,
    contentBlockProductsUrl,
    showVehicleModal,
    btnLabel = 'In Winkelwagen',
    btnTheme = 'callout',
    btnIcon = 'cart',
    disableModal = false,
    redirectUrl
}) {
    const [status, setStatus] = useState('idle');
    const [showModal, setShowModal] = useState(false);
    const resetTimerRef = useRef(null);

    const vehicleFilters = getShopData('vehicleFilters', null);
    const licensePlate = getShopData('licensePlate', '');

    useEffect(() => {
        toast.configure(reactToastifyConfig);
    }, []);

    // clear timeout als component unmount
    useEffect(() => {
        return () => clearTimeout(resetTimerRef.current);
    }, []);

    const handleClick = async (e) => {
        e.preventDefault();

        if (!product?.id || !shopwareUrl) {
            toast.error('Er is iets misgegaan bij het toevoegen aan de winkelwagen');
            return;
        }

        setStatus('loading');

        if (!isVehicleDataComplete() && showVehicleModal != null) {
            showVehicleModal(true);
        } else {
            try {
                await addToCart({ id: product.id, shopwareUrl });
                setStatus('added');

                if (redirectUrl) {
                    window.location.href = redirectUrl;
                }

                if (!disableModal && !redirectUrl) {
                    setShowModal(true);
                }

                resetTimerRef.current = setTimeout(() => setStatus('idle'), 3000);
            } catch (err) {
                if (err.name === 'DuplicateProductError') {
                    if (redirectUrl) {
                        window.location.href = redirectUrl;
                    } else {
                        toast.info('Dit product staat al in je winkelwagen');
                    }
                } else {
                    toast.error('Er is iets misgegaan bij het toevoegen aan de winkelwagen');
                }
                setStatus('idle');
            }
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const label = status === 'loading' ? 'Toevoegen…' : status === 'added' ? 'Toegevoegd' : btnLabel;
    const productImageSrc = product?.image;
    const displayName = product?.name;
    const displayNumber = product?.productNumber;
    const optimizedImageSrc = productImageSrc ? getCloudImageUrl(productImageSrc, {
        width: 150,
        height: 100
    }) : null;

    return (
        <>
            <Button
                className="btn-block"
                theme={btnTheme}
                text={label}
                icon={{ url: status === 'added' ? checkIcon : (btnIcon === 'arrow' ? arrowIcon : cartIcon) }}
                onClick={handleClick}
                disabled={status === 'loading'}
            />

            {!disableModal && (
                <Modal
                    className="modal--add-to-cart"
                    open={showModal}
                    handleClose={handleCloseModal}
                    title={trans('sulu.content_block.product_overview.modal.header.title')}
                    bodyContent={
                        <div className="p-8 bg-light">
                            <h4 className="mb-2">{trans('sulu.content_block.product_overview.modal.body.title')}</h4>
                            <div className="d-flex flex-row gap-2 align-items-center">
                                { productImageSrc && (
                                    <div className="product-image">
                                        <img
                                            src={optimizedImageSrc}
                                            alt={displayName}
                                            className="img-fluid rounded"
                                            width="75"
                                            height="50"
                                        />
                                    </div>
                                )}
                                <div className="product-details">
                                    <p className="font-weight-bold text--14 mb-0">{displayName}</p>
                                    <p className="text--14">{displayNumber}</p>
                                </div>
                            </div>
                            <div className="mt-4 d-flex align-items-center gap-4">
                                <Button
                                    theme="callout"
                                    text={trans('sulu.content_block.product_overview.modal.btn.shop.title')}
                                    onClick={handleCloseModal}
                                />
                                <Button
                                    theme="text-base"
                                    text={trans('sulu.content_block.product_overview.modal.btn.cart.title')}
                                    href={cartUrl}
                                />
                            </div>
                        </div>
                    }
                >
                    <RecommendedAccessories
                        productId={product.id}
                        shopwareUrl={shopwareUrl}
                        relatedProductsUrl={relatedProductsUrl}
                        contentBlockProductsUrl={contentBlockProductsUrl}
                        vehicleFilters={vehicleFilters}
                        licensePlate={licensePlate}
                    />
                </Modal>
            )}
        </>
    );
}
